import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Star, TrendingUp, ExternalLink, MessageSquare } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReviewsTab = () => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/admin/reviews?days=${days}`);
        setData(res.data);
      } catch (e) { console.error(e); }
    };
    fetch();
  }, [days]);

  if (!data) return <div className="text-center py-12 text-[#6E5E52]">Chargement...</div>;

  const { reviews, stats } = data;

  return (
    <div className="space-y-4">
      {/* Header + Stats */}
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-[#2D241D]" data-testid="reviews-title">
              Avis clients ({stats.total})
            </h3>
            <p className="text-xs text-[#6E5E52]">
              Notes recueillies avant paiement | {stats.google_redirected} rediriges vers Google
            </p>
          </div>
          <select value={days} onChange={(e) => setDays(parseInt(e.target.value))}
            className="h-9 rounded-xl border border-[#E5DCD3] px-3 bg-white text-sm text-[#2D241D]">
            <option value={7}>7 jours</option>
            <option value={30}>30 jours</option>
            <option value={90}>90 jours</option>
            <option value={365}>1 an</option>
          </select>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-[#F3EDE5] rounded-xl p-3 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Star className="w-5 h-5 fill-[#D4A853] text-[#D4A853]" />
              <span className="text-2xl font-bold text-[#2D241D]">{stats.average}</span>
            </div>
            <p className="text-[10px] text-[#6E5E52]">Note moyenne</p>
          </div>
          <div className="bg-[#F3EDE5] rounded-xl p-3 text-center">
            <span className="text-2xl font-bold text-[#2D241D]">{stats.total}</span>
            <p className="text-[10px] text-[#6E5E52]">Avis total</p>
          </div>
          <div className="bg-[#E8F5E9] rounded-xl p-3 text-center">
            <span className="text-2xl font-bold text-[#2D6A4F]">{stats.google_redirected}</span>
            <p className="text-[10px] text-[#2D6A4F]">Vers Google</p>
          </div>
          <div className="bg-[#FFF3E0] rounded-xl p-3 text-center">
            <span className="text-2xl font-bold text-[#E16833]">
              {stats.total > 0 ? Math.round((stats.google_redirected / stats.total) * 100) : 0}%
            </span>
            <p className="text-[10px] text-[#E16833]">Taux conversion</p>
          </div>
        </div>

        {/* Star distribution */}
        <div className="mt-4 space-y-1">
          {[5, 4, 3, 2, 1].map(s => {
            const count = stats.by_star[s] || 0;
            const pct = stats.total > 0 ? (count / stats.total) * 100 : 0;
            return (
              <div key={s} className="flex items-center gap-2 text-sm">
                <span className="w-4 text-right text-[#6E5E52] font-medium">{s}</span>
                <Star className="w-3.5 h-3.5 fill-[#D4A853] text-[#D4A853]" />
                <div className="flex-1 bg-[#E5DCD3] rounded-full h-2">
                  <div className={`h-2 rounded-full ${s >= 4 ? 'bg-[#2D6A4F]' : s === 3 ? 'bg-[#D4A853]' : 'bg-[#E63946]'}`}
                    style={{ width: `${pct}%` }} />
                </div>
                <span className="w-8 text-right text-xs text-[#6E5E52]">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Reviews list */}
      <div className="space-y-3">
        {reviews.map(review => (
          <div key={review.id} className={`bg-white rounded-2xl p-4 border ${review.rating >= 4 ? 'border-[#E5DCD3]' : 'border-red-200'}`}
            data-testid={`review-${review.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map(s => (
                  <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'fill-[#D4A853] text-[#D4A853]' : 'text-[#E5DCD3]'}`} />
                ))}
              </div>
              <div className="flex items-center gap-2">
                {review.redirected_to_google && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#E8F5E9] text-[#2D6A4F] font-semibold flex items-center gap-0.5">
                    <ExternalLink className="w-3 h-3" /> Google
                  </span>
                )}
                <span className="text-xs text-[#6E5E52]">
                  Table {review.table_id} | {new Date(review.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
            {review.comment && (
              <p className="text-sm text-[#2D241D] flex items-start gap-2">
                <MessageSquare className="w-4 h-4 text-[#6E5E52] flex-shrink-0 mt-0.5" />
                {review.comment}
              </p>
            )}
            {!review.comment && <p className="text-xs text-[#6E5E52] italic">Pas de commentaire</p>}
          </div>
        ))}
        {reviews.length === 0 && (
          <div className="text-center py-12 text-[#6E5E52] bg-white rounded-2xl border border-[#E5DCD3]">
            <Star className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Aucun avis pour cette periode</p>
          </div>
        )}
      </div>
    </div>
  );
};
